/**
 * MentionTextarea Component
 * Textarea with @mention autocomplete support
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { userService } from '../../services/user.service';
import { Avatar } from './Avatar';
import { LoadingSpinner } from './LoadingSpinner';
import type { MentionUser } from '../../types/comment';

export interface MentionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  onMentionsChange: (users: MentionUser[]) => void;
  placeholder?: string;
  rows?: number;
  autoFocus?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  disabled?: boolean;
  maxLength?: number;
  showCharacterCount?: boolean;
}

export const MentionTextarea: React.FC<MentionTextareaProps> = ({
  value,
  onChange,
  onMentionsChange,
  placeholder,
  rows = 2,
  autoFocus,
  onKeyDown,
  disabled,
  maxLength = 5000,
  showCharacterCount = true,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionedUsers, setMentionedUsers] = useState<MentionUser[]>([]);

  // Search users query
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users', 'search', searchQuery],
    queryFn: () => userService.searchUsersForMention(searchQuery, 5),
    enabled: showDropdown && searchQuery.length >= 0,
    staleTime: 30000,
  });

  // Detect @ mentions in text
  const detectMention = useCallback((text: string, cursorPos: number) => {
    // Look backwards from cursor to find @
    let atIndex = -1;
    for (let i = cursorPos - 1; i >= 0; i--) {
      const char = text[i];
      if (char === '@') {
        atIndex = i;
        break;
      }
      // Stop at whitespace or newline before finding @
      if (char === ' ' || char === '\n') {
        break;
      }
    }

    if (atIndex >= 0) {
      const query = text.slice(atIndex + 1, cursorPos);
      // Only show dropdown if query doesn't contain spaces (partial name)
      // or contains one space (first + partial last name)
      const spaceCount = (query.match(/ /g) || []).length;
      if (spaceCount <= 1) {
        setMentionStartIndex(atIndex);
        setSearchQuery(query);
        setShowDropdown(true);
        setSelectedIndex(0);
        return;
      }
    }

    setShowDropdown(false);
    setMentionStartIndex(null);
  }, []);

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    const cursorPos = e.target.selectionStart;
    detectMention(newValue, cursorPos);
  };

  // Handle user selection from dropdown
  const selectUser = useCallback((user: MentionUser) => {
    if (mentionStartIndex === null || !textareaRef.current) return;

    const fullName = `@${user.firstName} ${user.lastName}`;
    const beforeMention = value.slice(0, mentionStartIndex);
    const afterMention = value.slice(textareaRef.current.selectionStart);
    const newValue = beforeMention + fullName + ' ' + afterMention;

    onChange(newValue);
    setShowDropdown(false);
    setMentionStartIndex(null);

    // Add to mentioned users if not already there
    if (!mentionedUsers.find(u => u.id === user.id)) {
      const newMentioned = [...mentionedUsers, user];
      setMentionedUsers(newMentioned);
      onMentionsChange(newMentioned);
    }

    // Focus back on textarea
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = mentionStartIndex + fullName.length + 1;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  }, [value, mentionStartIndex, mentionedUsers, onChange, onMentionsChange]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showDropdown && users.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % users.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + users.length) % users.length);
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        selectUser(users[selectedIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowDropdown(false);
        return;
      }
    }

    // Pass through other key events
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        textareaRef.current &&
        !textareaRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync mentioned users when value changes externally (e.g., cleared)
  useEffect(() => {
    if (value === '') {
      setMentionedUsers([]);
      onMentionsChange([]);
    }
  }, [value, onMentionsChange]);

  // Character count helpers
  const charCount = value.length;
  const isOverLimit = charCount > maxLength;
  const isNearLimit = charCount >= maxLength - 100 && charCount <= maxLength;

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        autoFocus={autoFocus}
        disabled={disabled}
        maxLength={maxLength}
        className={`w-full rounded-lg border px-3 py-2 pb-6 text-sm focus:outline-none focus:ring-1 disabled:bg-gray-50 disabled:text-gray-500 ${
          isOverLimit
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
        }`}
      />

      {/* Character Counter */}
      {showCharacterCount && (
        <div className="absolute bottom-2 right-2 pointer-events-none">
          <span
            className={`text-xs ${
              isOverLimit
                ? 'text-red-600 font-medium'
                : isNearLimit
                ? 'text-amber-600'
                : 'text-gray-400'
            }`}
          >
            {charCount}/{maxLength}
          </span>
        </div>
      )}

      {/* Mention Dropdown */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute left-0 z-50 mt-1 w-64 rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <LoadingSpinner size="sm" />
            </div>
          ) : users.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500">
              {searchQuery ? 'No users found' : 'Type to search users'}
            </div>
          ) : (
            <ul className="max-h-48 overflow-y-auto py-1">
              {users.map((user, index) => (
                <li
                  key={user.id}
                  onClick={() => selectUser(user)}
                  className={`flex cursor-pointer items-center gap-3 px-4 py-2 ${
                    index === selectedIndex
                      ? 'bg-primary-50 text-primary-900'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <Avatar
                    size="sm"
                    fallbackText={`${user.firstName} ${user.lastName}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user.firstName} {user.lastName}
                    </p>
                    {/*
                      Email is only returned to callers entitled to it; department
                      is the non-PII disambiguator for everyone else. Render nothing
                      rather than an empty line when neither is available.
                    */}
                    {(user.email || user.department) && (
                      <p className="text-xs text-gray-500 truncate">
                        {user.email || user.department}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
